/**
 * A People's Chant — placeholder page for the upcoming game.
 *
 * Currently a description / pitch deck.  Logged in the Journal entry
 * dated May 25, 2026 as a Settlers-of-the-Middle-Ages-style low-res
 * multiplayer clone.  No gameplay yet.
 *
 * Aesthetic mirrors the rest of the studio pages: dark parchment,
 * Cinzel display + Times New Roman body, gold accents, subtle vellum
 * vignette.  Borrows the Tavern's pale tint for the "vibe block"
 * card without committing to a final theme yet.
 */
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";

const ACCENT = "#E5C870";
const INK = "#F5F1E8";
const MUTED = "#9C9078";

const Card = ({ title, children, testId }) => (
  <section
    className="mb-6 p-5 rounded-sm border"
    style={{ borderColor: "rgba(229,200,112,0.18)", background: "rgba(255,255,255,0.015)" }}
    data-testid={testId}
  >
    {title ? (
      <div
        className="text-lg mb-3"
        style={{ color: ACCENT, fontFamily: "'Cinzel','Times New Roman',serif", fontWeight: 700, letterSpacing: "0.02em" }}
      >
        {title}
      </div>
    ) : null}
    <div
      className="chant-body"
      style={{
        color: "#D6CCB4", lineHeight: 1.7,
        fontFamily: "'Cinzel','Times New Roman',serif",
        fontSize: "0.95rem",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </div>
  </section>
);

export default function PeoplesChantPage() {
  const navigate = useNavigate();
  return (
    <div
      className="studio-home relative min-h-screen overflow-x-clip"
      data-testid="peoples-chant-page"
      style={{ background: "#0C0A08", color: INK }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 40% at 22% 18%, rgba(229,200,112,0.09), transparent 65%), radial-gradient(ellipse 60% 45% at 82% 82%, rgba(122,31,31,0.10), transparent 70%)",
        }}
      />
      <TopBar
        theme="studio"
        onBack={() => navigate("/")}
        backLabel="HOME"
        testId="peoples-chant-back-button"
      />
      <div className="relative z-10 min-h-screen flex flex-col md:flex-row max-w-7xl mx-auto">
        <div
          className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
          style={{ borderColor: "rgba(229,200,112,0.14)" }}
          data-testid="peoples-chant-left-pane"
        >
          <NavPane theme="studio" />
        </div>

        <main className="flex-1 flex flex-col pt-4 md:pt-6 px-6 md:px-12 pb-10">
          <div className="max-w-3xl">
            <h1
              className="leading-[1.02] mb-2"
              style={{
                fontFamily: "'UnifrakturCook','UnifrakturMaguntia','EB Garamond','Times New Roman',serif",
                fontWeight: 700,
                color: ACCENT,
                fontSize: "clamp(2.75rem, 5.5vw, 5.5rem)",
                letterSpacing: "0.02em",
                textShadow: "0 2px 0 rgba(0,0,0,0.55), 0 0 28px rgba(229,200,112,0.22)",
              }}
              data-testid="peoples-chant-title"
            >
              A People&rsquo;s Chant
            </h1>

            {/* Illuminated-manuscript style reference image, iter M641.
             * Centered between the title and the italic subtitle, framed
             * with a thin gilded border that picks up the page accent. */}
            <div
              className="my-6 flex justify-center"
              data-testid="peoples-chant-hero-frame"
            >
              <img
                src="/chant/generated_vielle.jpg"
                alt="A medieval street scene with a vielle player and onlookers"
                data-testid="peoples-chant-hero-img"
                style={{
                  display: "block",
                  maxWidth: "min(100%, 520px)",
                  width: "100%",
                  height: "auto",
                  border: `1px solid ${ACCENT}`,
                  boxShadow:
                    "0 4px 18px rgba(0,0,0,0.55), 0 0 0 4px rgba(229,200,112,0.05)",
                }}
              />
            </div>

            <div
              className="mb-8"
              style={{
                fontFamily: "'Times New Roman', Times, serif",
                fontStyle: "italic",
                fontWeight: 400,
                color: MUTED,
                fontSize: "clamp(1rem, 1.6vw, 1.4rem)",
                letterSpacing: "0.01em",
              }}
              data-testid="peoples-chant-subtitle"
            >
              An in-progress idea &mdash; a low-res, multi-player village-and-trade
              game in the spirit of The Settlers and Knights and Merchants.
            </div>

            <Card title="The pitch" testId="peoples-chant-pitch">
              <p>
                A 1920&times;1080 full-screen, low-resolution multiplayer
                economy &amp; settlement game.  Players steward villages
                through stretches of Early, High, and Late Middle Ages and
                into the Renaissance &mdash; orchestrating production
                chains, trade routes, and the slow drift of culture and
                technology.
              </p>
            </Card>

            <Card title="Influences" testId="peoples-chant-influences">
              <ul className="list-disc pl-5 space-y-1.5">
                <li>The Settlers &mdash; broad pacing, charm, hand-drawn world.</li>
                <li>
                  Knights and Merchants &mdash; granular production lines, the patient
                  satisfaction of watching a kingdom slowly come together.
                </li>
                <li>
                  Medieval and Renaissance ambient music &mdash; lutes, dulcimers,
                  shawms, distant choir.
                </li>
              </ul>
            </Card>

            <Card title="Open questions (work-in-progress)" testId="peoples-chant-todo">
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Main menu layout and what to put on it.</li>
                <li>Music direction across the four eras (motifs that grow up with the player&rsquo;s civilisation).</li>
                <li>Whether to commission or synthesise the worker / animal soundbites.</li>
                <li>
                  Player data &mdash; do we lean on the existing Mongo set-up
                  used by Gin Rummy, or shard per-server for the multiplayer
                  sessions?
                </li>
                <li>How many concurrent players a single map should comfortably host.</li>
                <li>A custom font for the game &mdash; period-appropriate, legible at low resolution.</li>
              </ul>
            </Card>

            <Card title="Status" testId="peoples-chant-status">
              <p>
                Pre-development. Notes are live in <a
                  href="/progress-diary"
                  style={{ color: ACCENT, textDecoration: "underline" }}
                >the Journal</a> as
                they accumulate; this page will fill in as the game takes shape.
              </p>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
